import type { StockProfileResponse } from '@shared/types';

interface PortfolioStockDetailsProps {
  symbol: string;
  profile?: StockProfileResponse;
  loading?: boolean;
  error?: string | null;
}

const PortfolioStockDetails = ({
  symbol,
  profile,
  loading,
  error,
}: PortfolioStockDetailsProps) => {
  const detailsWrap = 'mt-5 border-t border-border pt-[18px]';
  const loadingText = 'mt-3 text-[13px] text-muted-foreground';

  if (loading && !profile) {
    return (
      <div className={detailsWrap}>
        <p className={loadingText}>Loading company details…</p>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className={detailsWrap}>
        <p className={loadingText}>Company details are unavailable.</p>
      </div>
    );
  }

  const subtitleParts = [profile?.exchange, profile?.assetClass].filter(Boolean);

  return (
    <div className={detailsWrap}>
      <div className="flex items-center gap-3">
        {profile?.logoUrl && (
          <img
            src={profile.logoUrl}
            alt=""
            className="size-10 rounded-lg bg-white object-contain p-1"
            loading="lazy"
          />
        )}
        <div>
          <h3 className="m-0 font-display text-[1.05rem] font-semibold text-foreground">
            {profile?.name ?? symbol}
          </h3>
          {subtitleParts.length > 0 && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {subtitleParts.join(' · ')}
            </p>
          )}
        </div>
      </div>

      {profile?.industry && (
        <div className="mt-3.5 flex gap-2.5 text-[13px]">
          <span className="shrink-0 text-muted-foreground">Industry</span>
          <span className="text-foreground/85">{profile.industry}</span>
        </div>
      )}

      {profile?.description && (
        <p className="mt-3.5 text-[13px] leading-relaxed text-foreground/70">
          {profile.description}
        </p>
      )}

      {profile?.website && (
        <a
          href={profile.website}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3.5 inline-block text-[13px] text-primary hover:underline"
        >
          {profile.website.replace(/^https?:\/\//, '')}
        </a>
      )}

      {!profile?.industry && !profile?.description && !profile?.website && (
        <p className={loadingText}>
          Extended company details are not available for {symbol}.
        </p>
      )}
    </div>
  );
};

export default PortfolioStockDetails;
